import { useState } from 'react'
import { useAnalyticsDashboard } from 'AnalyticsDashboard/AnalyticsContext'
import { parse, parseISO, format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, subDays, subWeeks, subMonths, subQuarters, subYears } from 'date-fns'
import { Popover } from 'react-tiny-popover'
import AsyncSelect from 'Shared/components/AsyncSelect'
import FilterRules from 'AnalyticsDashboard/components/FilterRules/FilterRules'

function FilterControl({ filter, value, onChange }) {
  return (
    <>
      {filter.kind === 'datetime' && <FilterDateTimeRange value={value} onChange={onChange} />}
      {filter.kind === 'entity' && <FilterEntity multi={!!filter.multi} entityKind={filter.entityKind} value={value} onChange={onChange} />}
      {filter.kind === 'tags' && <FilterTags value={value} onChange={onChange} />}
      {filter.kind === 'rules' && <FilterRules value={value} onChange={onChange} />}
      {filter.kind === 'options' && <FilterOptions filter={filter} value={value} onChange={onChange} />}
    </>
  )
}

export function Filters({ filters, filterValues, setFilterValue, clearFilterValue }) {
  const [manualIds, setManualIds] = useState({})

  const expand = (id) => setManualIds(prev => ({ ...prev, [id]: true }))
  const collapse = (id) => setManualIds(prev => ({ ...prev, [id]: false }))

  const isExpanded = (f) => manualIds[f.id] ?? (filterValues[f.id] !== undefined && filterValues[f.id] !== null)
  const expanded = filters.filter(isExpanded)
  const collapsed = filters.filter(f => !isExpanded(f))

  return (
    <div className="query-builder__filters">
      {expanded.map((filter) => (
        <div key={filter.id} className="query-builder__filter">
          <div className="query-builder__filter-header">
            <span className="query-builder__filter-label">{filter.label || filter.id}</span>
            <button type="button" className="query-builder__filter-remove" onClick={() => { clearFilterValue(filter.id); collapse(filter.id) }}>
              Remove
            </button>
          </div>
          <div className="query-builder__filter-control">
            <FilterControl filter={filter} value={filterValues[filter.id]} onChange={(v) => setFilterValue(filter.id, v)} />
          </div>
        </div>
      ))}
      {collapsed.length > 0 && (
        <div className="query-builder__filters-collapsed">
          {collapsed.map((filter) => (
            <button key={filter.id} type="button" className="query-builder__filter-add" onClick={() => expand(filter.id)}>
              + {filter.label || filter.id}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function DashboardFilters() {
  const { filters, filterValues, setFilterValue, fetchDataAll } = useAnalyticsDashboard()

  return (
    <div className="options">
      <h3>Filters</h3>
      <Filters
        filters={filters}
        filterValues={filterValues}
        setFilterValue={setFilterValue}
        clearFilterValue={(id) => setFilterValue(id, null)}
      />
      <div className="actions">
        <button onClick={fetchDataAll}>Apply Filters</button>
      </div>
    </div>
  )
}

const DATE_PRESET_ROWS = [
  [
    { label: 'Today', getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
    { label: 'This Week', getRange: () => ({ from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: endOfWeek(new Date(), { weekStartsOn: 1 }) }) },
    { label: 'This Month', getRange: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
    { label: 'This Quarter', getRange: () => ({ from: startOfQuarter(new Date()), to: endOfQuarter(new Date()) }) },
    { label: 'This Year', getRange: () => ({ from: startOfYear(new Date()), to: endOfYear(new Date()) }) },
  ],
  [
    { label: 'Yesterday', getRange: () => {
      const yesterday = subDays(new Date(), 1)
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) }
    }},
    { label: 'Last Week', getRange: () => {
      const lastWeek = subWeeks(new Date(), 1)
      return { from: startOfWeek(lastWeek, { weekStartsOn: 1 }), to: endOfWeek(lastWeek, { weekStartsOn: 1 }) }
    }},
    { label: 'Last Month', getRange: () => {
      const lastMonth = subMonths(new Date(), 1)
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) }
    }},
    { label: 'Last Quarter', getRange: () => {
      const lastQuarter = subQuarters(new Date(), 1)
      return { from: startOfQuarter(lastQuarter), to: endOfQuarter(lastQuarter) }
    }},
    { label: 'Last Year', getRange: () => {
      const lastYear = subYears(new Date(), 1)
      return { from: startOfYear(lastYear), to: endOfYear(lastYear) }
    }},
  ],
  [
    { label: 'Previous 7 Days', getRange: () => ({ from: startOfDay(subDays(new Date(), 7)), to: null }) },
    { label: 'Previous 30 Days', getRange: () => ({ from: startOfDay(subDays(new Date(), 30)), to: null }) },
    { label: 'Previous Year', getRange: () => ({ from: startOfDay(subYears(new Date(), 1)), to: null }) },
  ],
]

const DATE_PRESETS = DATE_PRESET_ROWS.flat()

export function FilterDateTimeRange({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false)

  const handleFromChange = (e) => {
    const parsedValue = parse(e.target.value, "yyyy-MM-dd'T'HH:mm", new Date())
    const newFrom = parsedValue ? parsedValue.toISOString() : ''
    onChange({ ...value, from: newFrom })
  }

  const handleToChange = (e) => {
    const parsedValue = parse(e.target.value, "yyyy-MM-dd'T'HH:mm", new Date())
    const newTo = parsedValue ? parsedValue.toISOString() : ''
    onChange({ ...value, to: newTo })
  }

  const handlePresetClick = (preset) => {
    const range = preset.getRange()
    onChange({
      from: range.from.toISOString(),
      to: range.to ? range.to.toISOString() : null
    })
    setIsOpen(false)
  }

  const formatDisplayDate = (isoString) => {
    if (!isoString) return ''
    return format(parseISO(isoString), 'd MMM yyyy')
  }

  const getDisplayText = () => {
    if (!value?.from) return 'Select date range'

    const matchingPreset = DATE_PRESETS.find((preset) => {
      const range = preset.getRange()
      const presetFrom = startOfDay(range.from).getTime()
      const valueFrom = startOfDay(parseISO(value.from)).getTime()
      const presetTo = range.to ? startOfDay(range.to).getTime() : null
      const valueTo = value.to ? startOfDay(parseISO(value.to)).getTime() : null
      return presetFrom === valueFrom && presetTo === valueTo
    })

    if (matchingPreset) {
      const dateRange = value.to
        ? `${formatDisplayDate(value.from)} - ${formatDisplayDate(value.to)}`
        : `${formatDisplayDate(value.from)} onwards`
      return `${matchingPreset.label} (${dateRange})`
    }

    if (value.to) {
      return `${formatDisplayDate(value.from)} - ${formatDisplayDate(value.to)}`
    }
    return `${formatDisplayDate(value.from)} onwards`
  }

  const displayText = getDisplayText()

  return (
    <div className="filter-date-time-range">
      <Popover
        isOpen={isOpen}
        positions={['bottom', 'top', 'right', 'left']}
        padding={10}
        onClickOutside={() => setIsOpen(false)}
        containerStyle={{ zIndex: '1010' }}
        content={
          <div className="date-range-popover">
            <div className="date-range-presets">
              {DATE_PRESET_ROWS.map((row, i) => (
                <div key={i} className="date-range-preset-row">
                  {row.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      className="preset-button"
                      onClick={() => handlePresetClick(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <div className="date-range-custom">
              <div className="date-input-group">
                <label>From</label>
                <input
                  type="datetime-local"
                  value={value?.from ? format(parseISO(value.from), "yyyy-MM-dd'T'HH:mm") : ''}
                  onChange={handleFromChange}
                />
              </div>
              <div className="date-input-group">
                <label>To</label>
                <input
                  type="datetime-local"
                  value={value?.to ? format(parseISO(value.to), "yyyy-MM-dd'T'HH:mm") : ''}
                  onChange={handleToChange}
                />
              </div>
            </div>
          </div>
        }
      >
        <button
          type="button"
          className="date-range-trigger"
          onClick={() => setIsOpen(!isOpen)}
        >
          {displayText}
        </button>
      </Popover>
    </div>
  )
}

export function FilterEntity({ multi, entityKind, value, onChange }) {
  var baseEndpoint
  if (entityKind === 'client') {
    baseEndpoint = '/global_admin/api/clients'
  }

  return <AsyncSelect
    entity={entityKind}
    value={value}
    onChange={onChange}
    baseEndpoint={baseEndpoint}
    placeholder={`Select ${entityKind}`}
    multiple={multi}
    loadStaticList
  />
}

export function FilterOptions({ filter, value, onChange }) {
  const selected = value || []
  const toggle = (optValue) => {
    const next = selected.includes(optValue)
      ? selected.filter(v => v !== optValue)
      : [...selected, optValue]
    onChange(next.length > 0 ? next : null)
  }

  return (
    <div className="filter-options">
      {filter.options.map(opt => (
        <label key={opt.value} className="filter-options__option">
          <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
          {opt.label}
        </label>
      ))}
    </div>
  )
}

export function FilterTags({ value, onChange }) {
  const tagIds = value?.tagIds || []
  const mode = value?.mode || 'any'

  const handleChange = (newValue) => {
    onChange({ tagIds: newValue, mode })
  }

  const handleChangeMode = (e) => {
    const newMode = e.target.value
    onChange({ tagIds, mode: newMode })
  }

  return <div>
    <AsyncSelect
      entity="tag"
      value={tagIds}
      onChange={handleChange}
      placeholder="Select tags"
      multiple
      loadStaticList
    />
    <select value={mode} onChange={handleChangeMode}>
      <option value="any">Any</option>
      <option value="all">All</option>
    </select>
  </div>
}
